<script setup>
import { ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import ButtonV4 from 'next/button/Button.vue';

const props = defineProps({
  pixQrCode: {
    type: String,
    default: '',
  },
  pixCopyPaste: {
    type: String,
    default: '',
  },
  amountCents: {
    type: Number,
    default: 0,
  },
  dueDate: {
    type: String,
    default: '',
  },
});

const copied = ref(false);

const formatCurrency = cents => {
  return `R$ ${(cents / 100).toFixed(2)}`;
};

const formatDate = dateStr => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('pt-BR');
};

const copyPixCode = async () => {
  try {
    await navigator.clipboard.writeText(props.pixCopyPaste);
    copied.value = true;
    useAlert('PIX code copied!');
    setTimeout(() => {
      copied.value = false;
    }, 3000);
  } catch {
    useAlert('Failed to copy');
  }
};
</script>

<template>
  <div class="space-y-4 px-5">
    <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
      <div class="flex items-center gap-2">
        <span class="text-n-slate-11">
          {{ $t('BILLING_SETTINGS.INTERNAL.PIX.AMOUNT') }}:
        </span>
        <span class="font-semibold text-lg">
          {{ formatCurrency(amountCents) }}
        </span>
      </div>
      <span v-if="dueDate" class="text-n-slate-11">
        {{ $t('BILLING_SETTINGS.INTERNAL.PIX.DUE') }}: {{ formatDate(dueDate) }}
      </span>
    </div>

    <div v-if="pixQrCode" class="flex justify-center">
      <img
        :src="`data:image/png;base64,${pixQrCode}`"
        :alt="$t('BILLING_SETTINGS.INTERNAL.PIX.QR_ALT')"
        class="w-48 h-48 border border-n-weak rounded-lg"
      />
    </div>

    <div v-if="pixCopyPaste" class="space-y-2">
      <label class="text-xs text-n-slate-11">
        {{ $t('BILLING_SETTINGS.INTERNAL.PIX.COPY_PASTE') }}
      </label>
      <div class="flex items-center gap-2">
        <input
          type="text"
          :value="pixCopyPaste"
          readonly
          class="flex-1 px-3 py-2 text-xs bg-n-solid-3 border border-n-weak rounded-lg font-mono"
        />
        <ButtonV4 sm solid slate @click="copyPixCode">
          {{
            copied
              ? $t('BILLING_SETTINGS.INTERNAL.PIX.COPIED')
              : $t('BILLING_SETTINGS.INTERNAL.PIX.COPY')
          }}
        </ButtonV4>
      </div>
    </div>
  </div>
</template>
